import React, {useCallback, useState, useMemo, useEffect, useRef} from 'react';
import {Box, Grid} from '@mui/material';
import AurPagination from '../aur-pagination/AurPagination';
import {useComponentSize} from '../../../hooks/utils/useComponentSize';
import {getCardSize} from '../../../utils/utils';
import {t} from 'i18next';

interface CardData<T> {
  rows: T[];
  pageSizeOptions?: number[];
  entityType?: string;
  CardComponent: React.ComponentType<T>;
  showPagination?: boolean;
  onClickHandler?: (param: T) => void;
  totalRowCount?: number;
  currentPage: number;
  setCurrentPage: (arg0: number) => void;
  itemsPerPage: number;
  setItemsPerPage: (arg0: number) => void;
}

const THROTTLE_TIME = 100; // in ms
const CARD_MIN_WIDTH = 416; // in pixel

const AurCardGrid = <T extends Record<string, any>>({
  rows,
  pageSizeOptions = [12, 24, 36],
  entityType = t('common.clients'),
  CardComponent,
  showPagination = true,
  onClickHandler,
  totalRowCount,
  currentPage,
  setCurrentPage,
  itemsPerPage,
  setItemsPerPage,
}: CardData<T>) => {
  const {ref, size} = useComponentSize();
  const [cardSize, setCardSize] = useState<number>(0);
  const timeOutId = useRef<NodeJS.Timeout | null>(null);
  const totalItems = totalRowCount || rows?.length;

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((itemsPerPage: number) => {
    setItemsPerPage(itemsPerPage);
    setCurrentPage(1);
  }, []);

  const limitedPerPageOptions = useMemo(() => {
    const options = (pageSizeOptions as number[]).filter(
      (option) => option <= totalItems
    );
    const additionalOption = (pageSizeOptions as number[]).find(
      (option) => option >= totalItems
    );
    if (additionalOption && !options.includes(additionalOption)) {
      options.push(additionalOption);
    }
    return options;
  }, [pageSizeOptions, totalItems]);

  const gridStyles = {
    paddingTop: '40px',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
  };

  useEffect(() => {
    if (timeOutId.current) clearTimeout(timeOutId.current);
    timeOutId.current = setTimeout(() => {
      const cardWidth = getCardSize(size.width, CARD_MIN_WIDTH, 0);
      setCardSize(cardWidth);
    }, THROTTLE_TIME);
  }, [size]);
  return (
    <>
      <Grid container spacing={2} ref={ref} sx={gridStyles}>
        {rows?.map((row, index) => (
          <Grid
            item
            key={index}
            sx={{width: `${cardSize}px`, visibility: cardSize ? '' : 'hidden'}}
          >
            <Box
              tabIndex={0}
              onKeyDown={(e) => {
                e.key == 'Enter'
                  ? onClickHandler
                    ? onClickHandler(row)
                    : ''
                  : '';
              }}
              onClick={() => (onClickHandler ? onClickHandler(row) : '')}
            >
              <CardComponent {...row} /> {/* Spread props dynamically */}
            </Box>
          </Grid>
        ))}
      </Grid>
      {totalItems > 12 && showPagination && (
        <Grid container>
          <AurPagination
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            perPageOptions={limitedPerPageOptions}
            itemType={entityType}
            setItemsPerPage={handleItemsPerPageChange}
            onPageChange={handlePageChange}
            backgroundColor="#0C1119"
          />
        </Grid>
      )}
    </>
  );
};

export default AurCardGrid;
